import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { ActionItemStatus, markBackfillInventoryFormSchema } from '@ekhodealer/ekho-common';
import { Button, SkeletonRect,Stack, Text, TextInput, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { useGetMarkBackfillInventoryAddedToInventorySystem } from '../../../api/progress/useGetMarkBackfillInventoryAddedToInventorySystem';
import { useMarkBackfillInventoryAddedToInventorySystem } from '../../../api/progress/useMarkBackfillInventoryAddedToInventorySystem';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface MarkBackfillInventoryAddedToInventorySystemDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	purchaseOrderId: string;
}

/**
 * MarkBackfillInventoryAddedToInventorySystemDetail component for marking backfill inventory as added to inventory system.
 * Displays a text input for purchase order ID and a submit button.
 */
export const MarkBackfillInventoryAddedToInventorySystemDetail = ({
	actionItem,
}: MarkBackfillInventoryAddedToInventorySystemDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const markBackfillInventoryMutation = useMarkBackfillInventoryAddedToInventorySystem(orderId);
	const { data: existingInventoryData, isLoading: isLoadingInventoryData } =
		useGetMarkBackfillInventoryAddedToInventorySystem(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(markBackfillInventoryFormSchema),
		defaultValues: {
			purchaseOrderId: '',
		},
	});

	// Pre-populate form with existing inventory data
	useEffect(() => {
		if (existingInventoryData?.purchaseOrderId) {
			reset({
				purchaseOrderId: existingInventoryData.purchaseOrderId,
			});
		}
	}, [existingInventoryData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		try {
			await markBackfillInventoryMutation.mutateAsync({
				purchaseOrderId: data.purchaseOrderId.trim(),
			});
			toast({
				message: 'Backfill inventory marked as added to inventory system successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage =
				err instanceof Error
					? err.message
					: 'Failed to mark backfill inventory as added to inventory system';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Enter the purchase order ID from the inventory system. The date will be recorded automatically when you submit.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{isLoadingInventoryData ? (
					<SkeletonRect width='100%' height={48} />
				) : (
					<Controller
						name='purchaseOrderId'
						control={control}
						render={({ field: { onChange, onBlur, value } }) => (
							<Stack itemsSpacing='4'>
								<Text size='footnote'>Purchase order ID</Text>
								<TextInput
									placeholder='Enter purchase order ID'
									value={value}
									onChange={onChange}
									onBlur={onBlur}
									size='large'
									hasError={errors.purchaseOrderId?.message}
									isDisabled={
										isSubmitting ||
										markBackfillInventoryMutation.isPending ||
										actionItem.status === ActionItemStatus.COMPLETE
									}
								/>
								{errors.purchaseOrderId && (
									<Text size='footnote' color='error'>
										{errors.purchaseOrderId.message}
									</Text>
								)}
							</Stack>
						)}
					/>
				)}

				{actionItem.status !== ActionItemStatus.COMPLETE && (
					<Button
						size='large'
						isFullWidth
						onPress={() => {
							submitHandler();
						}}
						isLoading={isSubmitting || markBackfillInventoryMutation.isPending}
						isDisabled={isLoadingInventoryData}>
						{actionItem.config.buttonTitle || 'Mark as added to inventory system'}
					</Button>
				)}
			</Stack>
		</Stack>
	);
};
